import { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CardMedia from "../Media/CardMedia";
import SlideTrack from "./SlideTrack";
import { useSlideLock } from "../../hooks/useSlideLock";
import { ADS_CAMPAIGN_MEDIA_FROM_CREATIFY } from "../../utils/constants";

const CarouselDisplay = ({
  urls = [],
  mediaData = [],
  onRemove,
  itemSize = 160,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isSlideLocked, lockSlide } = useSlideLock();

  if (!urls || urls.length === 0) return null;

  const hasMultipleMedia = urls.length > 1;

  const onPrev = (e) => {
    if (e) e.stopPropagation();
    if (!lockSlide()) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : urls.length - 1));
  };

  const onNext = (e) => {
    if (e) e.stopPropagation();
    if (!lockSlide()) return;
    setCurrentIndex((prev) => (prev < urls.length - 1 ? prev + 1 : 0));
  };

  const slideUrls = urls.map((entry) =>
    typeof entry === "string" ? entry : entry?.url || entry
  );

  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === urls.length - 1;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: itemSize,
        minWidth: itemSize,
        height: itemSize,
        width: itemSize,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Close button */}
      {onRemove && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            position: "absolute",
            backgroundColor: "white !important",
            right: 4,
            top: 4,
            p: 0,
            width: itemSize <= 100 ? 18 : 24,
            height: itemSize <= 100 ? 18 : 24,
            zIndex: 2,
          }}
        >
          <CloseIcon
            color="primary"
            sx={{
              fontSize: itemSize <= 100 ? "0.75rem" : "1rem",
            }}
          />
        </IconButton>
      )}

      {/* Media display */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <SlideTrack
          urls={slideUrls}
          index={currentIndex}
          sx={{ width: "100%", height: "100%" }}
          renderSlide={(slideUrl, slideIndex, isActive) => {
            const slideMediaData =
              mediaData?.[slideIndex] ||
              (typeof urls[slideIndex] === "object"
                ? urls[slideIndex]?.mediaData
                : null) ||
              {};
            return slideMediaData?.from === ADS_CAMPAIGN_MEDIA_FROM_CREATIFY ? (
              <iframe
                src={slideMediaData?.preview}
                style={{
                  display: "block",
                  border: "none",
                  overflow: "hidden",
                  height: "100%",
                  width: "100%",
                }}
              />
            ) : (
              <CardMedia
                url={slideUrl}
                eager
                propsType={{
                  controls: false,
                  autoplay: isActive,
                }}
                sxImage={{
                  borderRadius: 2,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                sxVideo={{
                  borderRadius: "8px",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            );
          }}
        />

        {/* Navigation controls */}
        {hasMultipleMedia && (
          <>
            <IconButton
              onClick={onPrev}
              disabled={isFirstImage || isSlideLocked}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
                "&.Mui-disabled": {
                  opacity: 0.8,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  cursor: "not-allowed",
                },
                zIndex: 1,
                width: 32,
                height: 32,
                "& .MuiSvgIcon-root": {
                  fontSize: 18,
                },
              }}
              size="small"
            >
              <ChevronLeftIcon sx={{ color: "#fff !important" }} />
            </IconButton>
            <IconButton
              onClick={onNext}
              disabled={isLastImage || isSlideLocked}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
                "&.Mui-disabled": {
                  opacity: 0.8,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  cursor: "not-allowed",
                },
                zIndex: 1,
                width: 32,
                height: 32,
                "& .MuiSvgIcon-root": {
                  fontSize: 18,
                },
              }}
              size="small"
            >
              <ChevronRightIcon sx={{ color: "#fff !important" }} />
            </IconButton>

            {/* Position indicator */}
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                px: 2,
                py: 0.5,
                borderRadius: 2,
                fontSize: "12px",
                fontWeight: 500,
                color: "#fff !important",
                zIndex: 1,
              }}
            >
              {currentIndex + 1} / {urls.length}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CarouselDisplay;
